import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function HomePage() {

  return (
    <>
      <Helmet>
        <title>Azure Cost Calculator | GBP Pricing | AzureCalc.uk</title>
        <meta name="description" content="Free Azure cost calculator built for UK South — GBP pricing updated nightly from the Azure Retail Prices API. Log Analytics + Sentinel combined, App Service v4, Flex Consumption. No currency switching needed." />
        <link rel="canonical" href="https://www.azure-calc.co.uk" />
        <meta property="og:title" content="Azure Cost Calculator | GBP Pricing | AzureCalc.uk" />
        <meta property="og:description" content="Free Azure cost calculator built for UK South — GBP pricing updated nightly from the Azure Retail Prices API. Log Analytics + Sentinel combined." />
        <meta property="og:url" content="https://www.azure-calc.co.uk" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="AzureCalc.uk" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Azure Cost Calculator | GBP Pricing | AzureCalc.uk" />
        <meta name="twitter:description" content="Free Azure cost calculator built for UK South — GBP pricing updated nightly from the Azure Retail Prices API. No currency switching needed." />
        <script type="application/ld+json">{JSON.stringify([
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'AzureCalc.uk',
            url: 'https://www.azure-calc.co.uk',
            logo: 'https://www.azure-calc.co.uk/favicon.svg',
            description: 'Independent Azure cost calculator for UK South. Prices fetched nightly from the Microsoft Azure Retail Prices API in GBP.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'AzureCalc.uk',
            url: 'https://www.azure-calc.co.uk',
            description: 'Free independent Azure cost calculator for UK South.',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://www.azure-calc.co.uk/faq',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'AzureCalc.uk — Azure Cost Calculator UK',
            applicationCategory: 'FinanceApplication',
            operatingSystem: 'Web',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'GBP',
            },
            url: 'https://www.azure-calc.co.uk',
            description: 'Estimate Azure Blob Storage, Logic Apps, Functions, App Service, Bandwidth, Log Analytics and VM costs for UK South in GBP.',
          },
        ])}</script>
      </Helmet>

      <div className="p-4 md:p-8 flex flex-col gap-8">

        {/* HERO / CONTEXT SECTION */}
        <section className="border-b border-outline-variant/20 pb-8">
          <div className="flex flex-col gap-6 max-w-5xl">
            <div className="flex flex-col gap-3">
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-on-surface">
                Azure pricing for UK engineers
              </h1>
              <p className="text-on-surface-variant font-medium leading-relaxed max-w-2xl">
                The scenarios Microsoft's calculator handles poorly — in GBP, updated nightly.
              </p>

              <div className="flex items-center gap-3 px-3 py-2 rounded-sm" style={{ backgroundColor: 'rgba(0,120,212,0.08)', border: '1px solid rgba(0,120,212,0.2)' }}>
                <span className="font-mono text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                  UK South · GBP · Live from Azure Retail Prices API · Not affiliated with Microsoft
                </span>
              </div>
            </div>

            {/* Insight cards — specific pricing facts, not marketing */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                {
                  icon: 'restore',
                  fact: 'The 2TB restore minimum',
                  detail: 'Restoring even 1 byte of archived Log Analytics data costs a minimum of £204. The restore job consumes 2TB regardless of actual size.',
                  cta: 'Calculate restore cost →',
                  to: '/log-analytics',
                },
                {
                  icon: 'lan',
                  fact: 'NAT Gateway has no pause state',
                  detail: "An idle NAT Gateway charges £33/month until deleted. Unlike a VM, there's no deallocate option — billing only stops on deletion.",
                  cta: 'Estimate networking cost →',
                  to: '/networking',
                },
                {
                  icon: 'savings',
                  fact: 'Commitment tier break-even',
                  detail: 'At 85 GB/day, the 100 GB/day Log Analytics commitment tier saves £847/month vs PAYG. Most teams stay on PAYG past the break-even point.',
                  cta: 'Find your break-even →',
                  to: '/log-analytics',
                },
              ].map((card) => (
                <Link
                  key={card.fact}
                  to={card.to}
                  className="group flex flex-col gap-2 p-4 border border-outline-variant/30 rounded-sm hover:border-primary/50 hover:bg-surface-container/40 transition-all"
                >
                  <span className="material-symbols-outlined text-[20px] text-primary">{card.icon}</span>
                  <p className="text-[12px] font-bold text-on-surface leading-snug">{card.fact}</p>
                  <p className="text-[11px] text-on-surface-variant leading-relaxed flex-1">{card.detail}</p>
                  <span className="text-[10px] text-primary font-bold uppercase tracking-widest group-hover:underline mt-1">
                    {card.cta}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CALCULATOR GRID — all 24 services as cards */}
        <section>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'Blob Storage', icon: 'cloud', desc: 'Hot, Cool, Cold and Archive tiers · LRS/GRS/ZRS', path: '/blob-storage' },
              { name: 'App Service', icon: 'language', desc: 'B/P/S tiers, Windows/Linux, Shared and Dedicated', path: '/app-service' },
              { name: 'Azure Functions', icon: 'functions', desc: 'Consumption, Premium, and Dedicated plans', path: '/functions' },
              { name: 'Logic Apps', icon: 'router', desc: 'Consumption and Standard pricing models', path: '/logic-apps' },
              { name: 'Virtual Machines', icon: 'computer', desc: 'Pay-as-you-go and commitment-based pricing', path: '/virtual-machines' },
              { name: 'Bandwidth', icon: 'cloud_download', desc: 'Egress, inter-region, and ExpressRoute costs', path: '/bandwidth' },
              { name: 'Log Analytics & Sentinel', icon: 'analytics', desc: 'Ingestion tiers, search, restore, and commitment', path: '/log-analytics' },
              { name: 'Networking', icon: 'hub', desc: 'VNet, NAT Gateway, Load Balancer, VPN costs', path: '/networking' },
              { name: 'Data Factory', icon: 'data_usage', desc: 'Pipeline execution, data movement, integration runtime', path: '/data-factory' },
              { name: 'Virtual Desktop', icon: 'smart_display', desc: 'Per-user and external licensing costs', path: '/virtual-desktop' },
              { name: 'Front Door', icon: 'account_tree', desc: 'Standard and Premium SKUs, requests and data', path: '/front-door' },
              { name: 'SQL Database', icon: 'database', desc: 'Serverless, Elastic Pool, and Dedicated options', path: '/sql-database' },
              { name: 'Azure Firewall', icon: 'security', desc: 'Premium and Standard, threat intelligence, DNS proxy', path: '/firewall' },
              { name: 'Azure OpenAI', icon: 'smart_toy', desc: 'gpt-4o, gpt-4o-mini, o3-mini token pricing', path: '/azure-openai' },
              { name: 'Managed Disks', icon: 'save', desc: 'Premium, Standard, and Ultra disk types', path: '/managed-disks' },
              { name: 'Key Vault', icon: 'vpn_key', desc: 'Secrets, keys, certificates, and operations', path: '/key-vault' },
              { name: 'Azure DevOps', icon: 'construction', desc: 'Pipelines, test plans, and user licensing', path: '/azure-devops' },
              { name: 'Site Recovery', icon: 'health_and_safety', desc: 'VMware, Hyper-V, and Azure VM replication', path: '/site-recovery' },
              { name: 'Azure Kubernetes Service', icon: 'router', desc: 'Compute, storage, networking, and load balancing', path: '/aks' },
              { name: 'SQL Managed Instance', icon: 'database', desc: 'Compute, storage, and licensing per core', path: '/sql-managed-instance' },
              { name: 'Application Gateway', icon: 'api', desc: 'Standard v2 and WAF v2 pricing', path: '/application-gateway' },
              { name: 'VPN Gateway', icon: 'vpn_lock', desc: 'Basic, Standard, and High Performance SKUs', path: '/vpn-gateway' },
              { name: 'Azure Bastion', icon: 'castle', desc: 'Hourly compute and data transfer costs', path: '/bastion' },
              { name: 'Azure Backup', icon: 'shield_backup', desc: 'Protected instance and backup storage pricing', path: '/azure-backup' },
            ].map((calc) => (
              <Link
                key={calc.path}
                to={calc.path}
                className="group flex flex-col gap-2 p-4 border border-outline-variant/30 rounded-sm hover:border-primary/50 hover:bg-surface-container/40 transition-all"
              >
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[18px] text-primary">{calc.icon}</span>
                  <h3 className="text-[13px] font-bold text-on-surface">{calc.name}</h3>
                </div>
                <p className="text-[11px] text-on-surface-variant leading-relaxed flex-1">{calc.desc}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Footer note */}
        <div className="border-t border-outline-variant/20 pt-6 mt-6">
          <p className="text-[13px] text-on-surface-variant leading-relaxed mb-2">
            Prices sourced from the Azure Retail Prices API, updated nightly.
          </p>
          <p className="text-[13px] text-on-surface-variant leading-relaxed">
            Not affiliated with, endorsed by, or sponsored by Microsoft.
          </p>
        </div>

      </div>
    </>
  );
}
